import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatDate } from '@/lib/utils';

export function AdminPage() {
  const qc = useQueryClient();

  const users = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: async () => (await api.get('/api/admin/users')).data,
  });

  const apiKeys = useQuery({
    queryKey: ['admin', 'api-keys'],
    queryFn: async () => (await api.get('/api/admin/api-keys')).data,
  });

  const modelRegistry = useQuery({
    queryKey: ['admin', 'model-registry'],
    queryFn: async () => (await api.get('/api/admin/model-registry')).data,
  });

  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyValue, setNewKeyValue] = useState<string | null>(null);

  const createKey = useMutation({
    mutationFn: async () => api.post('/api/admin/api-keys', { name: newKeyName }),
    onSuccess: (res) => {
      setNewKeyValue(res.data.key as string);
      setNewKeyName('');
      qc.invalidateQueries({ queryKey: ['admin', 'api-keys'] });
    },
    onError: (err) => toast.error('Failed', extractApiError(err).message),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Admin</h1>
        <p className="text-sm text-muted-foreground">
          Tenant settings, users, API keys, and AI model routing.
        </p>
      </div>

      {/* Users */}
      <Card>
        <CardHeader>
          <CardTitle>Users</CardTitle>
          <CardDescription>
            Role-based access control per 21 CFR Part 11 and ISO 27001 A.9.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Active</TableHead>
                <TableHead>MFA</TableHead>
                <TableHead>E-sig agreed</TableHead>
                <TableHead>Last login</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.data?.items?.map((u: {
                id: string;
                email: string;
                firstName: string;
                lastName: string;
                role: string;
                isActive: boolean;
                mfaEnabled: boolean;
                signatureAgreed: boolean;
                lastLoginAt: string | null;
              }) => (
                <TableRow key={u.id}>
                  <TableCell className="font-medium">{u.email}</TableCell>
                  <TableCell>
                    {u.firstName} {u.lastName}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">{u.role}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={u.isActive ? 'success' : 'secondary'}>
                      {u.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>{u.mfaEnabled ? '✓' : '—'}</TableCell>
                  <TableCell>{u.signatureAgreed ? '✓' : '—'}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {formatDate(u.lastLoginAt)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* API keys */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle>API keys</CardTitle>
            <CardDescription>Programmatic access for integrations.</CardDescription>
          </div>
          <div className="flex items-end gap-2">
            <div>
              <Label htmlFor="keyName">New key name</Label>
              <Input
                id="keyName"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                className="w-56"
                placeholder="e.g. Integration sandbox"
              />
            </div>
            <Button
              disabled={!newKeyName || createKey.isPending}
              onClick={() => createKey.mutate()}
            >
              Create
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Prefix</TableHead>
                <TableHead>Last used</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {apiKeys.data?.items?.map((k: {
                id: string;
                name: string;
                prefix: string;
                lastUsedAt: string | null;
                expiresAt: string | null;
                isActive: boolean;
              }) => (
                <TableRow key={k.id}>
                  <TableCell className="font-medium">{k.name}</TableCell>
                  <TableCell>
                    <code className="text-xs">{k.prefix}...</code>
                  </TableCell>
                  <TableCell>{formatDate(k.lastUsedAt)}</TableCell>
                  <TableCell>{k.expiresAt ? formatDate(k.expiresAt) : 'Never'}</TableCell>
                  <TableCell>
                    <Badge variant={k.isActive ? 'success' : 'secondary'}>
                      {k.isActive ? 'Active' : 'Revoked'}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Model routing */}
      <Card>
        <CardHeader>
          <CardTitle>AI model routing</CardTitle>
          <CardDescription>
            Per-task primary model with automatic fallback. Changes audited.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Task</TableHead>
                <TableHead>Primary</TableHead>
                <TableHead>Fallbacks</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {modelRegistry.data?.items?.map((m: {
                taskKey: string;
                primaryProvider: string;
                primaryModel: string;
                fallbackProviders: string[];
                fallbackModels: string[];
                isActive: boolean;
              }) => (
                <TableRow key={m.taskKey}>
                  <TableCell className="font-medium">{m.taskKey}</TableCell>
                  <TableCell>
                    <Badge>{m.primaryProvider}</Badge>{' '}
                    <code className="text-xs">{m.primaryModel}</code>
                  </TableCell>
                  <TableCell className="text-xs">
                    {m.fallbackProviders.map((p, i) => (
                      <span key={i} className="mr-2">
                        {p}/{m.fallbackModels[i] ?? ''}
                      </span>
                    ))}
                  </TableCell>
                  <TableCell>
                    <Badge variant={m.isActive ? 'success' : 'secondary'}>
                      {m.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* New key modal */}
      <Dialog open={!!newKeyValue} onOpenChange={() => setNewKeyValue(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>API key created</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Copy this key now — it will not be shown again.
          </p>
          <code className="block overflow-x-auto break-all rounded-md bg-muted p-3 text-xs">
            {newKeyValue}
          </code>
          <DialogFooter>
            <Button onClick={() => setNewKeyValue(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
